import React, { useEffect, useRef, useMemo, useState } from 'react';
import { Polyline, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';

interface PolylineDecoratorProps {
  positions: L.LatLngExpression[];
  pathColor: string;
  mode?: 'tour' | 'transit-walking' | 'transit-vehicle';
  outerColor?: string;
  animated?: boolean;
}

interface Settings {
  outerWeight: number;
  innerWeight: number;
  dashArray?: [number, number];
  animSpeed?: number; // pixels per frame (~60fps)
}

function getSettings(zoom: number, mode: string = 'tour'): Settings {
  if (mode === 'transit-walking') {
    return { outerWeight: 4.4, innerWeight: 2.75, dashArray: [15, 30], animSpeed: 0.15 };
  }
  if (mode === 'transit-vehicle') {
    return { outerWeight: 4.4, innerWeight: 2.75, dashArray: [20, 40], animSpeed: 1.2 };
  }

  if (zoom >= 18) {
    return { outerWeight: 4.4, innerWeight: 2.75, dashArray: [15, 30], animSpeed: 0.25 };
  } else if (zoom >= 17) {
    return { outerWeight: 4.4, innerWeight: 2.475, dashArray: [12, 24], animSpeed: 0.25 };
  } else if (zoom >= 16) {
    return { outerWeight: 4.4, innerWeight: 2.2, dashArray: [10, 20], animSpeed: 0.2 };
  } else if (zoom >= 14) {
    return { outerWeight: 3.85, innerWeight: 1.925 };
  } else if (zoom >= 12) {
    return { outerWeight: 3.3, innerWeight: 1.65 };
  } else {
    return { outerWeight: 2.75, innerWeight: 1.375 };
  }
}

function lightenColor(hex: string, amount: number = 0.5): string {
  const cleanHex = hex.replace('#', '');
  const fullHex = cleanHex.length === 3
    ? cleanHex.split('').map(c => c + c).join('')
    : cleanHex;

  const r = parseInt(fullHex.substring(0, 2), 16);
  const g = parseInt(fullHex.substring(2, 4), 16);
  const b = parseInt(fullHex.substring(4, 6), 16);

  const newR = Math.round(r + (255 - r) * amount);
  const newG = Math.round(g + (255 - g) * amount);
  const newB = Math.round(b + (255 - b) * amount);

  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
}

function getZoomTier(zoom: number, mode: string): number {
  if (mode !== 'tour') return 0;
  if (zoom >= 18) return 18;
  if (zoom >= 17) return 17;
  if (zoom >= 16) return 16;
  if (zoom >= 14) return 14;
  if (zoom >= 12) return 12;
  return 0;
}

function positionsChanged(a: L.LatLngExpression[], b: L.LatLngExpression[]): boolean {
  if (a.length !== b.length) return true;
  if (a.length === 0) return false;
  const aFirst = a[0] as [number, number];
  const bFirst = b[0] as [number, number];
  const aLast = a[a.length - 1] as [number, number];
  const bLast = b[b.length - 1] as [number, number];
  return aFirst[0] !== bFirst[0] || aFirst[1] !== bFirst[1]
    || aLast[0] !== bLast[0] || aLast[1] !== bLast[1];
}

export const PolylineDecorator: React.FC<PolylineDecoratorProps> = React.memo(({
  positions,
  pathColor,
  mode = 'tour',
  outerColor,
  animated = true,
}) => {
  const map = useMap();
  const animLineRef = useRef<L.Polyline | null>(null);
  const rafRef = useRef<number>(0);
  const offsetRef = useRef<number>(0);
  const speedRef = useRef<number>(0.5);
  const prevPositionsRef = useRef<L.LatLngExpression[]>(positions);

  const [zoomTier, setZoomTier] = useState(() => getZoomTier(Math.round(map.getZoom()), mode));
  const zoomRef = useRef(Math.round(map.getZoom()));

  useMapEvents({
    zoomend: () => {
      const newZoom = Math.round(map.getZoom());
      zoomRef.current = newZoom;
      const newTier = getZoomTier(newZoom, mode);
      setZoomTier(prev => prev !== newTier ? newTier : prev);
    },
  });

  const settings = useMemo(() => getSettings(zoomRef.current, mode), [zoomTier, mode]);
  const innerColor = useMemo(() =>
    mode === 'tour' ? lightenColor(pathColor, 0.5) : pathColor,
    [pathColor, mode]
  );
  const resolvedOuterColor = outerColor || pathColor;
  const showAnimation = animated && (mode !== 'tour' || zoomTier >= 16);

  // Effect 1: Create/destroy the animated dashed polyline + rAF loop
  useEffect(() => {
    if (!map || !positions || positions.length < 2 || !showAnimation) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (animLineRef.current) {
        map.removeLayer(animLineRef.current);
        animLineRef.current = null;
      }
      return;
    }

    if (!animLineRef.current) {
      const dash = settings.dashArray || [10, 20];
      speedRef.current = settings.animSpeed || 0.5;

      const line = L.polyline(positions, {
        color: innerColor,
        weight: settings.innerWeight,
        opacity: 1,
        dashArray: dash.join(' '),
        lineCap: 'butt',
        lineJoin: 'round',
      });
      line.addTo(map);
      animLineRef.current = line;
      prevPositionsRef.current = positions;

      // rAF animation loop — runs independently of Leaflet redraws
      const animate = () => {
        const el = (line as any)._path as SVGPathElement | undefined;
        if (el) {
          offsetRef.current -= speedRef.current;
          el.style.strokeDashoffset = offsetRef.current + 'px';
        }
        rafRef.current = requestAnimationFrame(animate);
      };
      rafRef.current = requestAnimationFrame(animate);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (animLineRef.current) {
        map.removeLayer(animLineRef.current);
        animLineRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, showAnimation]);

  // Effect 2: Update positions in-place when route changes
  useEffect(() => {
    if (!animLineRef.current) return;
    if (!positionsChanged(prevPositionsRef.current, positions)) return;
    prevPositionsRef.current = positions;
    animLineRef.current.setLatLngs(positions);
  }, [positions]);

  // Effect 3: Update style in-place when zoom tier or colors change
  useEffect(() => {
    if (!animLineRef.current) return;
    const dash = settings.dashArray || [10, 20];
    speedRef.current = settings.animSpeed || 0.5;
    animLineRef.current.setStyle({
      color: innerColor,
      weight: settings.innerWeight,
      dashArray: dash.join(' '),
    });
  }, [settings, innerColor]);

  if (!positions || positions.length < 2) return null;

  // Static polylines (outer + inner) when animation is off
  if (!showAnimation) {
    return (
      <>
        <Polyline
          positions={positions}
          pathOptions={{
            color: resolvedOuterColor,
            weight: settings.outerWeight,
            opacity: 1,
            lineCap: 'round',
            lineJoin: 'round',
          }}
        />
        <Polyline
          positions={positions}
          pathOptions={{
            color: innerColor,
            weight: settings.innerWeight,
            opacity: 1,
            lineCap: 'round',
            lineJoin: 'round',
          }}
        />
      </>
    );
  }

  // Animated mode: static outer polyline + JS-animated dashed inner (managed by effects)
  return (
    <Polyline
      positions={positions}
      pathOptions={{
        color: resolvedOuterColor,
        weight: settings.outerWeight,
        opacity: 1,
        lineCap: 'round',
        lineJoin: 'round',
      }}
    />
  );
});

PolylineDecorator.displayName = 'PolylineDecorator';
